import { excuseApi } from "../commons/http";
import GovernmentInvoice from "./governmentInvoice";

class Invoice {
  constructor(data = {}) {
    this.mapData(data);
  }

  mapData(data = {}) {
    this.id = data.id;
    this.code = data.code;
    this.sequence = data.sequence;
    this.invoice_type = data.invoice_type;
    this.status = data.status;
    this.user_id = data.user_id;
    this.order_id = data.order_id;
    this.user_package_id = data.user_package_id;
    this.bank_transaction_id = data.bank_transaction_id;
    this.subtotal = data.subtotal;
    this.tax_amount = data.tax_amount;
    this.total = data.total;
    this.issue_date = data.issue_date;
    this.due_date = data.due_date;
    this.paid_date = data.paid_date;
    this.note = data.note;
    this.line_items = data.line_items || [];
    this.sources = data.sources || [];   // S20: which orders/packages it covers
    this.renewal = data.renewal || null; // S21: {package_name, price} for autofill
    this.user = data.user;
    this.government_invoice = data.government_invoice
      ? new GovernmentInvoice(data.government_invoice) : null;
    this.activities = data.activities || data.history || [];
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    return this;
  }

  async getById(id) {
    const res = await excuseApi({ path: `/invoices/${id}`, method: 'GET' });
    if (res.status === 200) {
      this.mapData(res.data.data);
      return this;   // model instance so .government_invoice has lifecycle methods
    }
    return null;
  }

  async createFromOrder(orderId, meta = {}) {
    const res = await excuseApi({
      path: '/invoices/from-order',
      method: 'POST',
      body: { order_id: orderId, ...meta },
    });
    if (res.status === 200 || res.status === 201) {
      this.mapData(res.data.data);
      return res.data.data;
    }
    return null;
  }

  async createFromUserPackage(userPackageId, meta = {}) {
    const res = await excuseApi({
      path: '/invoices/from-user-package',
      method: 'POST',
      body: { user_package_id: userPackageId, ...meta },
    });
    if (res.status === 200 || res.status === 201) {
      this.mapData(res.data.data);
      return res.data.data;
    }
    return null;
  }

  /** Edit a DRAFT invoice: line items and/or note/dates (S15). */
  async update(id, payload) {
    const res = await excuseApi({
      path: `/invoices/${id}`, method: 'PUT', body: payload,
    });
    if (res && res.status === 200) {
      this.mapData(res.data.data);
      return res.data.data;
    }
    return null;
  }

  async updateStatus(id, status) {
    const res = await excuseApi({
      path: `/invoices/${id}/status`,
      method: 'PATCH',
      body: { status },
    });
    if (res.status === 200) {
      return res.data.data;
    }
    return null;
  }

  // Internal invoice as a receipt-style IMAGE with a VietQR payment QR (PNG blob).
  async getPdf(id) {
    const res = await excuseApi({ path: `/invoices/${id}/pdf`, method: 'GET', responseType: 'blob' });
    return res.status === 200 ? res.data : null;
  }

  // Manual bank-transaction matching (link a transfer -> PAID -> HDDT).
  async bankMatchCandidates(id) {
    const res = await excuseApi({ path: `/invoices/${id}/bank-match-candidates`, method: 'GET' });
    return res.status === 200 ? res.data : { data: [], invoice_total: 0 };
  }

  async matchTransaction(id, bankTransactionId) {
    const res = await excuseApi({
      path: `/invoices/${id}/match-transaction`, method: 'POST',
      body: { bank_transaction_id: bankTransactionId },
    });
    return res.status === 200 ? res.data.data : null;
  }

  // Re-pull the order's current line items into a DRAFT order-invoice.
  /** Re-pull the package's current settlement lines into this invoice. */
  async syncFromUserPackage(id) {
    const res = await excuseApi({ path: `/invoices/${id}/sync-from-user-package`, method: 'POST' });
    return res && res.status === 200 ? res.data.data : null;
  }

  async syncFromOrder(id) {
    const res = await excuseApi({ path: `/invoices/${id}/sync-from-order`, method: 'POST' });
    return res.status === 200 ? res.data.data : null;
  }

  // Compose (manual) invoice — FINV-02-S08
  async composePreview(payload) {
    const res = await excuseApi({
      path: '/invoices/compose/preview',
      method: 'POST',
      body: payload,
    });
    if (res.status === 200 || res.status === 201) {
      return res.data.line_items || [];
    }
    return [];
  }

  async compose(payload) {
    const res = await excuseApi({
      path: '/invoices/compose',
      method: 'POST',
      body: payload,
    });
    if (res.status === 200 || res.status === 201) {
      this.mapData(res.data.data);
      return res.data.data;
    }
    return null;
  }
}

export default Invoice;
